import { Formik } from "formik";
import { useEffect, useState } from "react";

import { Alert, Heading, SpotIcon, addToast } from "@icg360/design-system";
import { PAYMENT_COMPLETE, PAYMENT_ERROR } from "@icg360/payment-toolkit-v2";

import UnauthLayout from "components/shared/unauth-layout/index";
import { policyZipCodeSchema } from "consts";
import {
  acceptPaymentOnCancelledPolicy,
  addDays,
  payControl,
  scrollToTop,
  trackEvent,
} from "utils";

import AltPaymentBanner from "./alt-payment-banner";
import { BillPayForm } from "./bill-pay-form";
import { usePaymentInfo } from "./hooks";

const TOAST_DURATION = 10000; // ten seconds

export const BillPay = () => {
  const { verify } = usePaymentInfo();
  const [showMoratoriumAlert, setShowMoratoriumAlert] = useState(false);
  const [showNoBalanceAlert, setShowNoBalanceAlert] = useState(false);

  const handleError = (error: string, isWarning = false) => {
    trackEvent("Error Displayed (Payment)", { error });
    addToast(error, {
      icon: true,
      error: !isWarning,
      warning: isWarning,
      duration: TOAST_DURATION,
    });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    const onPaymentComplete = (response) => {
      (document.getElementById("bill-payment") as HTMLFormElement)?.reset();
      trackEvent("Submit Express Payment", response);
    };

    const onPaymentError = () => {
      addToast(
        "There was an error while submitting. Please refresh your browser and try again.",
        {
          icon: true,
          error: true,
        },
      );
      trackEvent("Payment Error (unauthenticated)");
    };

    return () => {
      payControl.addEventListener(PAYMENT_COMPLETE, onPaymentComplete);
      payControl.addEventListener(PAYMENT_ERROR, onPaymentError);
    };
  }, []);

  const handleSubmit = async ({ policyNumber, zipCode }) => {
    setShowMoratoriumAlert(false);
    trackEvent("Start Making Express Payment");

    const { data, error } = await verify(policyNumber, zipCode);

    if (error || !data) {
      handleError(error);
      return;
    }

    const {
      policyId,
      quoteId,
      insightPolicyId,
      email,
      phone,
      accountBalance,
      minAmountDue,
      policyState,
      totalPremium,
      policyStateAttributes,
      isMoratorium,
    } = data;

    if (isMoratorium) {
      setShowMoratoriumAlert(true);
      return;
    }

    const { reasonCode, effectiveDate } = policyStateAttributes ?? {};
    const cancelledButEligibleForRenewal = acceptPaymentOnCancelledPolicy(
      policyState,
      reasonCode?.value,
      effectiveDate,
      minAmountDue,
      isMoratorium,
    );

    if (!cancelledButEligibleForRenewal) {
      if (policyState !== "ACTIVEPOLICY") {
        handleError(
          "Looks like your policy is no longer active. Please contact your insurance representative for assistance.",
          true,
        );
        return;
      } else if (accountBalance <= 0) {
        trackEvent("No balance due alert shown (Payment)");
        setShowNoBalanceAlert(true);
        return;
      }
    }

    const thirtyDaysFromToday = addDays(Date.now(), 30);
    const finalPayDate = new Date(thirtyDaysFromToday)
      .toISOString()
      .split("T")[0];
    const minAmountDueScheduled = minAmountDue < 1 ? 1 : minAmountDue;

    payControl.makePayment({
      minAmountDue: Number(minAmountDueScheduled),
      accountBalance: Number(
        cancelledButEligibleForRenewal ? totalPremium : accountBalance,
      ),
      insightPolicyId,
      paymentApplicationClient: "2000",
      policyId,
      email,
      phone,
      quoteId,
      isScheduledPayEnabled: true,
      finalPayDate,
    });
  };

  return (
    <>
      <UnauthLayout>
        <SpotIcon name="Bill" appearance="bold" />
        <Heading size="lg">Pay my bill</Heading>
        {showMoratoriumAlert ? (
          <Alert
            appearance="warning"
            title="Payments are temporarily disabled:"
            description="You can’t make a payment right now, because your area is under a moratorium. Contact your insurance rep for help."
          />
        ) : null}
        <Formik
          initialValues={{
            policyNumber: "",
            zipCode: "",
          }}
          onSubmit={handleSubmit}
          validationSchema={policyZipCodeSchema}
        >
          <BillPayForm />
        </Formik>
        {showNoBalanceAlert ? (
          <Alert
            title="You're all set:"
            description="You don't have a balance right now. Thanks for keeping your account up to date!"
            appearance="info"
            onDismiss={() => setShowNoBalanceAlert(false)}
            dismissible
          />
        ) : null}
      </UnauthLayout>
      <AltPaymentBanner />
    </>
  );
};
